// Os ajustes de mês de um alvo da EAP (design D20) e as contas em R$ dos campos de valor.
// Meses em "AAAA-MM", dinheiro em centavos inteiros, nada de `Date`.
//
// Desde a D21 o ajuste mês a mês é uma regra com base na referência (`referenceSchedule.js`);
// aqui ficam o resumo dos ajustes de mês que restam (recorte do gráfico e os gravados antes
// da D21), as séries e os campos em reais.
import { compareMonthKey, shiftMonth } from "./hierarchy";

const COMPETENCE = /^\d{4}-(0[1-9]|1[0-2])$/;

/** `[["AAAA-MM", centavos]]` (a forma da rota) → `Map`, sem zeros. */
export const seriesMap = (pairs) => {
  const map = new Map();
  for (const pair of Array.isArray(pairs) ? pairs : []) {
    const [month, cents] = Array.isArray(pair)
      ? pair
      : [pair?.month ?? pair?.competence, pair?.cents];
    if (
      !COMPETENCE.test(String(month ?? "")) ||
      !Number.isSafeInteger(cents) ||
      cents === 0
    )
      continue;
    map.set(month, (map.get(month) ?? 0) + cents);
  }
  return map;
};

/** Cada mês de `first` a `last`, inclusive. */
export const monthsBetween = (first, last) => {
  const months = [];
  if (
    !COMPETENCE.test(String(first ?? "")) ||
    !COMPETENCE.test(String(last ?? ""))
  )
    return months;
  for (
    let month = first;
    compareMonthKey(month, last) <= 0;
    month = shiftMonth(month, 1)
  ) {
    months.push(month);
    // Guarda contra uma janela absurda vinda de fora: 50 anos de linhas não é uma tabela.
    if (months.length > 600) break;
  }
  return months;
};

/**
 * O que um conjunto de movimentos (as entradas ativas de um alvo) faz com a curva: quantos
 * meses mudam e quanto sai de um mês. É o resumo da linha do alvo na lista e na EAP.
 */
export const adjustmentSummary = (entries) => {
  const net = new Map();
  let exceedsCents = 0;
  for (const entry of entries ?? []) {
    if (!Number.isSafeInteger(entry?.amountCents)) continue;
    net.set(
      entry.fromCompetence,
      (net.get(entry.fromCompetence) ?? 0) - entry.amountCents,
    );
    net.set(
      entry.toCompetence,
      (net.get(entry.toCompetence) ?? 0) + entry.amountCents,
    );
    if (Number.isSafeInteger(entry?.exceedsCents))
      exceedsCents += entry.exceedsCents;
  }
  const changed = [...net].filter(([, cents]) => cents !== 0);
  return {
    entryCount: (entries ?? []).length,
    monthCount: changed.length,
    movedCents: changed.reduce(
      (sum, [, cents]) => sum + (cents > 0 ? cents : 0),
      0,
    ),
    exceedsCents,
  };
};

/** As entradas ativas de ajuste de mês agrupadas por nó: `Map<wbsCodeId, entradas[]>`. */
export const adjustmentsByNode = (adjustments) => {
  const byNode = new Map();
  for (const entry of adjustments ?? []) {
    if (entry?.active === false || !entry?.wbsCodeId) continue;
    const list = byNode.get(entry.wbsCodeId);
    if (list) list.push(entry);
    else byNode.set(entry.wbsCodeId, [entry]);
  }
  return byNode;
};

export const monthsText = (count) =>
  `${count} ${count === 1 ? "mês" : "meses"}`;

/** `"1.500,00"` → `150000`; `"0"`/`"0,00"` → `0`; o resto → `NaN`. */
export const parseReaisToCents = (input) => {
  const text = String(input ?? "").trim();
  if (/^0+(,0*)?$/.test(text)) return 0;
  if (!/^\d{1,3}(\.\d{3})*(,\d{0,3})?$|^\d+(,\d{0,3})?$/.test(text)) return NaN;
  const [whole, fraction = ""] = text.replace(/\./g, "").split(",");
  const padded = `${fraction}000`.slice(0, 3);
  const cents =
    Number(whole) * 100 +
    Number(padded.slice(0, 2)) +
    (Number(padded.slice(2, 3)) >= 5 ? 1 : 0);
  return Number.isSafeInteger(cents) ? cents : NaN;
};

/** `150000` → `"1500,00"` (o texto que o campo mostra). */
export const centsToReaisInput = (cents) =>
  (cents / 100).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: false,
  });

/**
 * R$ sem passar pelo contexto do projeto (`money()` lê contexto por dentro e não pode ser
 * chamado um número variável de vezes por render). O ajuste é sempre em reais.
 */
export const reais = (cents) =>
  Number.isFinite(cents)
    ? (cents / 100).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : "—";
